import styled from "styled-components";

/**
 * Visual tokens of the approved prototype, aligned with the platform palette:
 * `--nav` is the same navy used by the app bar and by every DataTable header.
 */
export const Container = styled.div`
  --nav: #002951;
  --ink: #212121;
  --ink-soft: #5f6368;
  --muted: #9aa0a6;
  --line: #e0e0e0;
  --panel: #ffffff;
  --soft: #f7f8fa;
  --blue: #1976d2;
  --heat-best: #57bb8a;
  --heat-good: #9ace6a;
  --heat-neutral: #ffe98a;
  --heat-warning: #fdb56b;
  --heat-critical: #e8705f;

  color: var(--ink);
  padding: 0 8px 64px;

  /* region Header line */
  /*
   * What is on screen on the left, the two panel actions on the right. No card around it: the row
   * is a caption plus two buttons, not data, and a white card on a white page would draw a border
   * around mostly empty space.
   */
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 18px;
    margin-bottom: 16px;
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  /* The drawer button carries the navy of the app bar, like every primary action of the platform. */
  .toolbar-actions .MuiButton-contained {
    background: var(--nav);
    color: #fff;
    box-shadow: none;
  }

  .toolbar-actions .MuiButton-contained:hover {
    background: #00224a;
  }

  .period-line {
    color: var(--ink-soft);
    font-size: 13px;
    margin: 0;
  }

  .period-line b {
    color: var(--ink);
    font-weight: 500;
  }
  /* endregion Header line */

  /* region Period warning */

  .period-warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12.5px;
    color: #8a5a00;
    background: #fff8e6;
    border: 1px solid #ffe2a8;
    border-radius: 6px;
    padding: 8px 12px;
    margin: 0 0 16px;
  }
  /* endregion Period warning */

  /* region Gauges */
  .gauges {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 14px;
    margin-bottom: 16px;
  }

  .gauge {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 14px 12px 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    text-align: center;
  }

  .gauge h3 {
    margin: 0 0 1px;
    font-size: 13px;
    font-weight: 500;
  }

  .gauge .gauge-subtitle {
    font-size: 11.5px;
    color: var(--muted);
    margin-bottom: 2px;
  }

  .gauge svg {
    width: 100%;
    max-width: 230px;
    height: auto;
    display: block;
    margin: 0 auto;
  }

  .gauge .gauge-value {
    font-size: 26px;
    font-weight: 500;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
    margin-top: -27px;
  }

  .gauge .gauge-value .unit {
    font-size: 13px;
    color: var(--ink-soft);
    font-weight: 400;
    margin-left: 3px;
  }

  .gauge .gauge-status {
    font-size: 11.5px;
    margin-top: 6px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--ink-soft);
  }

  .gauge .gauge-status i {
    width: 9px;
    height: 9px;
    border-radius: 2px;
    display: inline-block;
  }
  /* endregion Gauges */

  /* region Totals */
  .tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px;
    margin-bottom: 22px;
  }

  .tile {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 13px 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .tile .tile-label {
    font-size: 12px;
    color: var(--ink-soft);
    margin-bottom: 6px;
  }

  .tile .tile-value {
    font-size: 22px;
    font-weight: 500;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }

  .tile .unit {
    font-size: 13px;
    color: var(--ink-soft);
    font-weight: 400;
    margin-left: 3px;
  }

  .tile .tile-foot {
    font-size: 11.5px;
    color: var(--muted);
    margin-top: 5px;
  }
  /* endregion Totals */

  /* region Panels */
  .panel-row {
    display: grid;
    grid-template-columns: 1.15fr 0.85fr;
    gap: 16px;
    margin-bottom: 22px;
  }

  @media (max-width: 1100px) {
    .panel-row {
      grid-template-columns: 1fr;
    }
  }

  .panel {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    margin-bottom: 22px;
  }

  .panel-row .panel {
    margin-bottom: 0;
  }

  .panel-head {
    padding: 13px 18px;
    border-bottom: 1px solid var(--line);
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .panel-head h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
  }

  .panel-head p {
    margin: 3px 0 0;
    font-size: 12.5px;
    color: var(--ink-soft);
  }

  .panel-head .panel-head-main {
    flex: 1;
  }

  .panel-body {
    padding: 16px 18px;
  }

  .panel-empty {
    color: var(--muted);
    font-size: 13px;
    padding: 26px 0;
    text-align: center;
  }
  /* endregion Panels */

  /* region Ranking */
  .ranking {
    display: flex;
    flex-direction: column;
    gap: 3px;
    max-height: 330px;
    overflow-y: auto;
    padding-right: 4px;
  }

  .ranking-row {
    display: grid;
    grid-template-columns: 84px 1fr 58px;
    align-items: center;
    gap: 9px;
    font-size: 12px;
  }

  .ranking-row .ranking-name {
    color: var(--ink-soft);
    text-align: right;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ranking-row .ranking-track {
    background: var(--soft);
    border-radius: 3px;
    height: 15px;
    position: relative;
  }

  .ranking-row .ranking-fill {
    display: block;
    height: 100%;
    border-radius: 3px;
    min-width: 2px;
  }

  .ranking-row .ranking-value {
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
  }

  .ranking-median {
    position: absolute;
    top: -3px;
    bottom: -3px;
    width: 2px;
    background: var(--ink-soft);
    opacity: 0.65;
  }
  /* endregion Ranking */

  /* region Daily series */
  .days {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    height: 230px;
    padding-top: 6px;
  }

  .day {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    height: 100%;
    justify-content: flex-end;
  }

  .day-bar {
    width: 100%;
    background: var(--blue);
    opacity: 0.82;
    border-radius: 3px 3px 0 0;
    min-height: 2px;
  }

  .day-bar.consolidating {
    background: repeating-linear-gradient(
      45deg,
      #b7c7da,
      #b7c7da 4px,
      #e4ebf3 4px,
      #e4ebf3 8px
    );
    border: 1px solid #b7c7da;
    opacity: 1;
  }

  /* A day with no operation keeps its slot on the axis but draws nothing. */
  .day-bar.empty {
    min-height: 0;
    background: none;
    border: none;
  }

  .day-value {
    font-size: 11px;
    color: var(--ink-soft);
    font-variant-numeric: tabular-nums;
  }

  .day-value.no-value {
    color: var(--muted);
  }

  .day-label {
    font-size: 11px;
    color: var(--muted);
    text-align: center;
  }

  .day-label .consolidating-mark {
    display: block;
    font-size: 9.5px;
    color: #8a5a00;
  }

  .day-metrics {
    display: flex;
    gap: 10px;
    margin-top: 9px;
  }

  .day-metrics > div {
    flex: 1;
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  .day-foot {
    font-size: 11.5px;
    color: var(--muted);
    margin-top: 7px;
    text-align: center;
  }
  /* endregion Daily series */

  /* region Tables */
  /*
   * Both tables are DataTables (@molecules/NewDataTable), which collapses a column into an
   * expandable line instead of scrolling sideways and brings the ordering per column. The molecule
   * pulls itself up by 18px — meant for a page where it sits right under the header — so the panel
   * gives it back the room it needs.
   */
  .panel-table {
    padding: 22px 10px 6px;
  }

  .panel-table td {
    font-size: 13.5px;
    font-variant-numeric: tabular-nums;
  }

  .vehicle-name {
    font-weight: 500;
  }

  .vehicle-origins {
    display: block;
    font-size: 11px;
    color: var(--muted);
    font-weight: 400;
  }

  .vehicle-type {
    font-size: 11.5px;
    color: var(--ink-soft);
  }

  .heat-cell {
    display: block;
    border-radius: 3px;
    padding: 5px 8px;
    font-weight: 500;
  }

  .heat-cell.best { background: var(--heat-best); }
  .heat-cell.good { background: var(--heat-good); }
  .heat-cell.neutral { background: var(--heat-neutral); }
  .heat-cell.warning { background: var(--heat-warning); }
  .heat-cell.critical { background: var(--heat-critical); }

  .no-value {
    color: var(--muted);
  }

  .low-sample {
    font-size: 10.5px;
    color: var(--muted);
    border: 1px solid var(--line);
    border-radius: 3px;
    padding: 1px 5px;
    margin-left: 6px;
    white-space: nowrap;
  }

  .legend {
    display: flex;
    gap: 20px;
    align-items: center;
    flex-wrap: wrap;
    padding: 13px 18px;
    border-top: 1px solid var(--line);
    font-size: 12.5px;
    color: var(--ink-soft);
  }

  .legend-scale {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .legend-swatches {
    display: flex;
  }

  .legend-swatches i {
    width: 26px;
    height: 13px;
    display: block;
  }

  .legend-consolidating i {
    display: inline-block;
    width: 22px;
    height: 12px;
    margin-right: 6px;
    vertical-align: -2px;
    border: 1px solid #b7c7da;
    background: repeating-linear-gradient(
      45deg,
      #b7c7da,
      #b7c7da 4px,
      #e4ebf3 4px,
      #e4ebf3 8px
    );
  }
  /* endregion Tables */

  @media (max-width: 820px) {
    .toolbar {
      gap: 14px;
    }

    .filter-group {
      flex: 1 1 100%;
    }

    .days {
      height: 190px;
      gap: 6px;
    }
  }
`;

/** Body of the alert-limits modal, rendered inside a MUI Dialog (outside `Container`). */
export const ThresholdsContainer = styled.div`
  --line: #e0e0e0;
  --ink-soft: #5f6368;
  --muted: #9aa0a6;
  --heat-warning: #fdb56b;
  --heat-critical: #e8705f;

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;

  .threshold {
    border: 1px solid var(--line);
    border-radius: 6px;
    padding: 13px 15px;
  }

  .threshold h3 {
    margin: 0 0 2px;
    font-size: 13px;
    font-weight: 500;
  }

  .threshold h3 span {
    color: var(--muted);
    font-weight: 400;
  }

  .threshold .threshold-hint {
    font-size: 11.5px;
    color: var(--muted);
    margin-bottom: 11px;
  }

  .threshold .threshold-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 9px;
  }

  .threshold .threshold-row > span {
    font-size: 11.5px;
    color: var(--ink-soft);
    width: 72px;
    display: inline-flex;
    align-items: center;
  }

  .threshold .threshold-row .swatch {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 2px;
    margin-right: 5px;
  }

  .threshold .threshold-row .swatch.warn { background: var(--heat-warning); }
  .threshold .threshold-row .swatch.critical { background: var(--heat-critical); }

  .threshold .threshold-row .slider {
    flex: 1;
    min-width: 60px;
  }

  .threshold .threshold-row .number {
    width: 84px;
  }
`;
